import styled, { css } from 'styled-components';
import Required from '../components/required';
import RadioGroup from '../components/radioGroup';
import CardImg from '../images/card.svg';
import Check from '../images/check.svg';
import ACHImg from '../images/ach.svg';
import AcceptedCards from '../images/accepted_cards.png';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import Error from '../components/error';
import Button from '../components/button';
import { useForm, Controller } from 'react-hook-form';
import MaskedInput from 'react-text-mask/dist/reactTextMask';
import { createNumberMask } from 'text-mask-addons';
import Api from '../utils/api';
import Terms from '../components/terms';
import { useSearchParams } from 'react-router';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import * as sdkUtils from 'smartystreets-javascript-sdk-utils';
import { Autocomplete, TextField } from '@mui/material';

const numberMask = createNumberMask({
    prefix: '',
    suffix: '',
    allowDecimal: true,
    decimalLimit: 2,
});

const options = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'currency',
    currency: 'USD',
};
const formatter = new Intl.NumberFormat('en-US', options);

const CustomMaskedCCInput = forwardRef(({ onChange, value, ...props }, ref) => {
    const [mask, setMask] = useState(getCardMask(value || ''));

    useEffect(() => {
        setMask(getCardMask(value || ''));
    }, [value]);

    return (
        <MaskedInput
            {...props}
            mask={mask}
            ref={(inputRef) => {
                ref(inputRef ? inputRef.inputElement : null);
            }}
            onChange={(e) => {
                const newValue = e.target.value.replace(/\s/g, '');
                setMask(getCardMask(newValue));
                onChange(e);
            }}
        />
    );
});

export const getCardMask = (cardNumber) => {
    const visaMasterCardDiscoverMask = [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
    ];
    const amexMask = [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        /\d/,
    ];
    const dinersClubMask = [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
    ];
    const jcbMask = [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
    ];

    if (/^3[47]/.test(cardNumber)) {
        // American Express
        return amexMask;
    } else if (/^3(?:0[0-5]|[68])/.test(cardNumber)) {
        // Diners Club
        return dinersClubMask;
    } else if (/^35/.test(cardNumber)) {
        // JCB
        return jcbMask;
    } else if (/^4/.test(cardNumber) || /^5[1-5]/.test(cardNumber) || /^6(?:011|5)/.test(cardNumber)) {
        // Visa, MasterCard, Discover
        return visaMasterCardDiscoverMask;
    } else {
        // Default mask (Visa, MasterCard, Discover)
        return visaMasterCardDiscoverMask;
    }
};

const Payment = ({ details, update = false, onCancel, onSubmit }) => {
    const [showTerms, setShowTerms] = useState(false);
    const [selectedOption, setSelectedOption] = useState('ach');
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [paymentType, setPaymentType] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const [sameAsEfa, setSameAsEfa] = useState(searchParams.get('id') ? true : false);
    const amountType = searchParams.get('amount_type') || 'advance_payment';
    const [suggestions, setSuggestions] = useState({ result: [] });
    const [selectedAddress, setSelectedAddress] = useState(null);

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        getValues,
        clearErrors,
        control,
        formState: { errors },
    } = useForm();
    const options = [
        { value: 'ach', label: 'ACH', image: <img src={ACHImg} alt={'ach'} /> },
        {
            value: 'card',
            label: 'Credit Card',
            image: <img src={CardImg} alt={'card'} />,
        },
    ];

    const SmartyCore = SmartySDK.core;
    const websiteKey = process.env.REACT_APP_SMARTY;
    const smartySharedCredentials = new SmartyCore.SharedCredentials(websiteKey);

    const autoCompleteClient = new SmartyCore.ClientBuilder(smartySharedCredentials)
        .withLicenses(['us-autocomplete-pro-cloud'])
        .buildUsAutocompleteProClient();

    const createUSLookup = (query, hasSecondaries) => {
        const lookup = new SmartySDK.usAutocompletePro.Lookup(query);
        if (hasSecondaries) {
            lookup.selected = query;
        }
        return lookup;
    };

    const queryAutocompleteForSuggestions = async (query, addressId, hasSecondaries = false) => {
        if (query !== '') {
            const client = autoCompleteClient;
            const lookup = createUSLookup(query, hasSecondaries);
            lookup.country = 'US';
            try {
                const results = await client.send(lookup);
                setSuggestions(results);
            } catch (error) {
                console.warn(error);
            }
        } else {
            setSuggestions({ result: [] });
        }
    };

    const optionLabel = (op) => {
        let returnStr = '';
        if (op.streetLine) {
            returnStr += op.streetLine;
        }
        if (op.city) {
            returnStr += ', ' + op.city;
        }
        if (op.state) {
            returnStr += ', ' + op.state;
        }
        if (op.zipCode) {
            returnStr += ',' + op.zipCode;
        }

        return returnStr;
    };

    const compareOptions = (option, value) => {
        return (
            option.streetLine === value.streetLine &&
            option.city === value.city &&
            option.state === value.state &&
            option.zipCode === value.zipCode
        );
    };

    useEffect(() => {
        if (amountType) {
            const getType = details.response.amounts.find((a) => a.type === amountType);
            if (getType) {
                handleOptionClick(getType.type, getType.amount);
            }
        }
    }, [amountType, details]);

    useEffect(() => {
        if (details && sameAsEfa) {
            setValue('billing_address', details.address);
            setValue('billing_city', details.city);
            setValue('billing_state', details.state);
            setValue('billing_zip', details.zip);
            setValue('account_holder', details.account_holder);
        }
    }, [details, sameAsEfa, setValue]);

    const accountnum = watch('account_number');
    const amount = watch('amount');

    const paymentResponseHandler = (response) => {
        if (response.messages.resultCode === 'Error') {
            let i = 0;
            while (i < response.messages.message.length) {
                setError(response.messages.message[i].text);
                i = i + 1;
            }
        } else {
            const d = getValues();
            delete d.cardnumber;
            delete d.cvc;
            d.token = response.opaqueData.dataValue;
            submitPayment(d);
        }
    };

    const handleOptionClick = (type, amount) => {
        setPaymentType(type);
        setOptionsOpen(false);
        setValue('amount', `${formatter.format(amount / 100).replace('$', '')}`);
    };

    const submitPayment = useCallback(
        async (d) => {
            d = {
                ...d,
                ...details,
                type: selectedOption,
                payment_type: paymentType,
            };

            // Override custom
            if (paymentType === 'custom') {
                d.payment_type = 'other';
            }

            delete d.response;
            const url = update ? '/payment-method-update' : '/payment';
            if (!update) {
                // clean amount
                d.amount = d.amount.replaceAll(',', '').replace('$', '');
                if (selectedOption === 'card') {
                    // add 3% surcharge
                    d.amount = Number((d.amount * 1.03).toFixed(2));
                } else {
                    d.amount = Number(d.amount);
                }
            }
            setLoading(true);
            try {
                await Api.post(url, d);
                if (update) {
                    onSubmit('pay-update');
                } else {
                    if (selectedOption === 'card') {
                        onSubmit('pay-success');
                    } else {
                        onSubmit('pay-pending');
                    }
                }
            } catch (e) {
                if (e.response?.data && e.response.data.error) {
                    setError(e.response.data.error);
                } else {
                    setError('Unable to process.');
                }
            }
            setLoading(false);
        },
        [selectedOption, update, paymentType, amount]
    );

    const submit = useCallback(
        async (d) => {
            setLoading(true);
            if (selectedOption === 'card') {
                let authData = {
                    clientKey: process.env.REACT_APP_ACCEPT_KEY,
                    apiLoginID: process.env.REACT_APP_ACCEPT_ID,
                };
                const cardData = {
                    cardNumber: d.cardnumber.replaceAll(' ', ''),
                    month: d.month,
                    year: d.year.replace('20', ''),
                    cardCode: d.cvc,
                };
                const secureData = { authData, cardData };
                window.Accept.dispatchData(secureData, paymentResponseHandler);
            } else {
                await submitPayment(d);
            }
            setLoading(false);
        },
        [selectedOption, update, submitPayment, paymentType]
    );

    useEffect(() => {
        clearErrors();
    }, [selectedOption]);

    if (showTerms) {
        return (
            <>
                <Terms />
                <Button onClick={() => setShowTerms(false)}>Close</Button>
            </>
        );
    }

    return (
        <PageContainer onSubmit={handleSubmit(submit)}>
            <h2>{update ? 'Update Payment Profile' : 'Payment Details'}</h2>
            <Required>
                <span>i</span>All fields are required
            </Required>
            <RadioGroup options={options} value={selectedOption} onChange={setSelectedOption} horizontal />
            {searchParams.get('id') && (
                <CheckboxContainer>
                    <Checkbox type="checkbox" checked={sameAsEfa} onChange={(e) => setSameAsEfa(e.target.checked)} />
                    <label>Same information as EFA</label>
                </CheckboxContainer>
            )}
            {!update && (
                <FieldContainer>
                    <Label>Payment Amount</Label>
                    <PaymentSelect error={errors.amount}>
                        <div
                            style={{
                                color: '#7E57C2',
                                textTransform: 'capitalize',
                                flex: 1,
                                fontSize: '15px',
                                justifyContent: 'flex-start',
                            }}
                            onClick={() => setOptionsOpen(!optionsOpen)}
                        >
                            {paymentType === null
                                ? 'SELECT YOUR PAYMENT TYPE'
                                : paymentType === 'custom'
                                  ? 'Custom'
                                  : details.response.amounts.find((a) => a.type === paymentType).description}
                            <Arrow isOpen={optionsOpen} />
                        </div>
                        <FieldContainer>
                            <AmountInput
                                mask={numberMask}
                                disabled={paymentType !== 'custom'}
                                {...register('amount', { required: true })}
                                error={errors.amount}
                            />
                            <Prefix>$</Prefix>
                        </FieldContainer>
                        <PaymentOptions isOpen={optionsOpen}>
                            {details.response.amounts
                                .filter((a) => a.amount > 0)
                                .map((amount) => (
                                    <PaymentOption
                                        onClick={() => handleOptionClick(amount.type, amount.amount)}
                                        selected={paymentType === amount.type}
                                        key={amount.type}
                                    >
                                        <div>{amount.description}</div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '5px',
                                            }}
                                        >
                                            {formatter.format(Number(amount.amount / 100))}
                                            {amount.type === paymentType && <img src={Check} />}
                                        </div>
                                    </PaymentOption>
                                ))}
                            <PaymentOption
                                onClick={() => handleOptionClick('custom')}
                                selected={paymentType === 'custom'}
                            >
                                <div>Custom</div>
                                {paymentType === 'custom' && <img src={Check} />}
                            </PaymentOption>
                        </PaymentOptions>
                    </PaymentSelect>
                </FieldContainer>
            )}
            {selectedOption === 'card' && (
                <>
                    <i
                        style={{
                            fontSize: '12px',
                            textAlign: 'center',
                            marginBottom: '15px',
                        }}
                    >
                        Please note that there's a 3% surcharge when paying with a credit card.
                        {!update && (
                            <>
                                <br />
                                Total charge: &nbsp;
                                {amount && !isNaN(Number(amount.replaceAll(',', '')))
                                    ? formatter.format(amount.replaceAll(',', '') * 1.03)
                                    : '$0.00'}
                            </>
                        )}
                    </i>
                    <FieldContainer>
                        <div
                            style={{
                                flex: 1,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Label>Card Number</Label>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                }}
                            >
                                <Input
                                    style={{ flex: 1 }}
                                    as={CustomMaskedCCInput}
                                    {...register('cardnumber', {
                                        required: true,
                                    })}
                                    error={errors.cardnumber}
                                />
                                <img src={AcceptedCards} alt={'accepted cards'} height={'30px'} />
                            </div>
                        </div>
                    </FieldContainer>
                    <DualField>
                        <FieldContainer>
                            <Label>Expiration Date</Label>
                            <InputContainer error={errors.month || errors.year}>
                                <InputContained
                                    placeholder={'MM'}
                                    maxLength={2}
                                    {...register('month', { required: true })}
                                />
                                <InputContained
                                    placeholder={'YYYY'}
                                    maxLength={4}
                                    {...register('year', { required: true })}
                                />
                            </InputContainer>
                        </FieldContainer>
                        <FieldContainer>
                            <Label>CVC Security</Label>
                            <Input {...register('cvc', { required: true })} error={errors.cvc} />
                        </FieldContainer>
                    </DualField>
                    <FieldContainer>
                        <Label>Name on Card</Label>
                        <Input {...register('account_holder', { required: true })} error={errors.account_holder} />
                    </FieldContainer>
                </>
            )}
            {selectedOption === 'ach' && (
                <>
                    <FieldContainer>
                        <Label>Account Type</Label>
                        <Select
                            {...register('account_structure', {
                                required: true,
                            })}
                            error={errors.account_type}
                        >
                            <option value="Business">Business</option>
                            <option value="Personal">Personal</option>
                        </Select>
                    </FieldContainer>
                    <FieldContainer>
                        <Label>Account Structure</Label>
                        <Select
                            {...register('account_type', {
                                required: true,
                            })}
                            error={errors.account_structure}
                        >
                            <option value="Checking">Checking</option>
                            <option value="Savings">Savings</option>
                        </Select>
                    </FieldContainer>
                    <FieldContainer>
                        <Label>Name on Account</Label>
                        <Input {...register('account_holder', { required: true })} error={errors.account_holder} />
                    </FieldContainer>
                    <FieldContainer>
                        <Label>Routing Number</Label>
                        <Input {...register('routing_number', { required: true })} error={errors.routing_number} />
                    </FieldContainer>
                    <FieldContainer>
                        <Label>Account Number</Label>
                        <Input
                            {...register('account_number', {
                                required: true,
                                minLength: 4,
                            })}
                            error={errors.account_number}
                        />
                    </FieldContainer>
                    <FieldContainer>
                        <Label>Re-Enter Account Number</Label>
                        <Input
                            {...register('accountnumberconf', {
                                required: true,
                                minLength: 4,
                                validate: (value) => value === accountnum || 'Accounts do not match',
                            })}
                            error={errors.accountnumberconf}
                        />
                    </FieldContainer>
                </>
            )}
            {!sameAsEfa && (
                <>
                    <FieldContainer>
                        <Label>Address</Label>
                        <Controller
                            name="billing_address"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Autocomplete
                                    size={'small'}
                                    autoComplete
                                    onChange={(event, newValue) => {
                                        if (newValue) {
                                            setSelectedAddress(newValue);
                                            setValue('billing_city', newValue.city);
                                            setValue('billing_state', newValue.state);
                                            setValue('billing_zip', newValue.zipcode);
                                            field.onChange(newValue.streetLine);
                                        }
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        queryAutocompleteForSuggestions(newInputValue);
                                        field.onChange(newInputValue);
                                    }}
                                    isOptionEqualToValue={(option, value) => compareOptions(option, value)}
                                    getOptionLabel={optionLabel}
                                    options={suggestions.result}
                                    renderInput={(params) => (
                                        <StyledTextField
                                            {...params}
                                            size={'small'}
                                            error={errors.billing_address}
                                            inputRef={params.InputProps.ref}
                                        />
                                    )}
                                />
                            )}
                        />
                    </FieldContainer>
                    <DualField>
                        <FieldContainer>
                            <Label>City</Label>
                            <Input {...register('billing_city', { required: true })} error={errors.billing_city} />
                        </FieldContainer>
                        <FieldContainer>
                            <Label>Zip</Label>
                            <Input
                                as={CustomMaskedInput}
                                mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                                {...register('billing_zip', { required: true })}
                                error={errors.billing_zip}
                            />
                        </FieldContainer>
                    </DualField>
                    <FieldContainer>
                        <Label>State</Label>
                        <Select {...register('billing_state', { required: true })} error={errors.billing_state}>
                            <option value="">Select a state</option>
                            <option value="AL">Alabama</option>
                            <option value="AK">Alaska</option>
                            <option value="AZ">Arizona</option>
                            <option value="AR">Arkansas</option>
                            <option value="CA">California</option>
                            <option value="CO">Colorado</option>
                            <option value="CT">Connecticut</option>
                            <option value="DE">Delaware</option>
                            <option value="DC">District Of Columbia</option>
                            <option value="FL">Florida</option>
                            <option value="GA">Georgia</option>
                            <option value="HI">Hawaii</option>
                            <option value="ID">Idaho</option>
                            <option value="IL">Illinois</option>
                            <option value="IN">Indiana</option>
                            <option value="IA">Iowa</option>
                            <option value="KS">Kansas</option>
                            <option value="KY">Kentucky</option>
                            <option value="LA">Louisiana</option>
                            <option value="ME">Maine</option>
                            <option value="MD">Maryland</option>
                            <option value="MA">Massachusetts</option>
                            <option value="MI">Michigan</option>
                            <option value="MN">Minnesota</option>
                            <option value="MS">Mississippi</option>
                            <option value="MO">Missouri</option>
                            <option value="MT">Montana</option>
                            <option value="NE">Nebraska</option>
                            <option value="NV">Nevada</option>
                            <option value="NH">New Hampshire</option>
                            <option value="NJ">New Jersey</option>
                            <option value="NM">New Mexico</option>
                            <option value="NY">New York</option>
                            <option value="NC">North Carolina</option>
                            <option value="ND">North Dakota</option>
                            <option value="OH">Ohio</option>
                            <option value="OK">Oklahoma</option>
                            <option value="OR">Oregon</option>
                            <option value="PA">Pennsylvania</option>
                            <option value="RI">Rhode Island</option>
                            <option value="SC">South Carolina</option>
                            <option value="SD">South Dakota</option>
                            <option value="TN">Tennessee</option>
                            <option value="TX">Texas</option>
                            <option value="UT">Utah</option>
                            <option value="VT">Vermont</option>
                            <option value="VA">Virginia</option>
                            <option value="WA">Washington</option>
                            <option value="WV">West Virginia</option>
                            <option value="WI">Wisconsin</option>
                            <option value="WY">Wyoming</option>
                        </Select>
                    </FieldContainer>
                </>
            )}
            {error && (
                <Error>
                    <span>X</span> {error}
                </Error>
            )}

            <CheckboxContainer style={{ marginTop: '25px' }}>
                <Checkbox type="checkbox" {...register('terms', { required: true })} error={errors.terms} />
                <label>
                    I agree to the{' '}
                    <strong onClick={() => setShowTerms(true)} style={{ cursor: 'pointer' }}>
                        Terms and Conditions
                    </strong>
                </label>
            </CheckboxContainer>

            {selectedOption === 'ach' && (
                <CheckboxContainer>
                    <Checkbox
                        type="checkbox"
                        defaultChecked={true}
                        error={errors.default}
                        {...register('default', { required: update })}
                    />
                    <label>Set this as my default payment method for scheduled payments</label>
                </CheckboxContainer>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant={'contained'} type="submit" loading={loading} disabled={Object.keys(errors).length > 0}>
                    Next
                </Button>
            </div>
        </PageContainer>
    );
};

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    & strong {
        color: #7e57c2;
    }
`;
const Checkbox = styled.input`
    appearance: none;
    height: 20px;
    width: 20px;
    border: 2px solid #7e57c2;

    &:checked {
        background: #7e57c2;
    }

    ${({ error }) =>
        error &&
        css`
            border: 2px solid #d90000;
        `}
`;

const InputContained = styled.input`
    border: none;
    padding: 0;
    margin: 0;

    &:focus {
        outline: none;
    }
`;

const InputContainer = styled.div`
    padding: 10px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;
    display: flex;
    gap: 10px;
    flex-direction: row;

    ${({ error }) =>
        error &&
        css`
            border: 2px solid #d90000;
            padding: 9px;
        `}
    &:focus-within {
        outline: none;
        border: 2px solid #4a154b;
        padding: 9px;
    }

    :first-child {
        width: 50px;
        border-right: 1px solid rgba(0, 0, 0, 0.2);
    }

    :last-child {
        flex: 1;
    }
`;

const Arrow = styled.span`
    border: solid #7c5dfa;
    border-width: 0 2px 2px 0;
    display: inline-block;
    transform: rotate(45deg);
    height: 6px;
    width: 6px;
    margin-bottom: 2px;
`;

const Select = styled.select`
    padding: 10px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;

    ${({ error }) =>
        error &&
        css`
            border: 2px solid #d90000;
            padding: 9px;
        `}
    &:focus {
        outline: none;
        border: 2px solid #4a154b;
        padding: 9px;
    }
`;

const Label = styled.div`
    color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
`;

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
`;

const PageContainer = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    h2 {
        margin: 0;
    }
`;

const CustomMaskedInput = forwardRef((props, ref) => {
    return (
        <MaskedInput
            {...props}
            ref={(inputRef) => {
                ref(inputRef ? inputRef.inputElement : null);
            }}
        />
    );
});

const AmountInput = styled(CustomMaskedInput)`
    padding: 10px 10px 10px 25px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-size: 18px;
    font-weight: 600;
    color: #00838f;
    box-sizing: border-box;
    position: relative;

    &:disabled {
        background-color: transparent;
        border: none;
    }

    ${({ error }) =>
        error &&
        css`
            border: 2px solid #d90000;
            padding: 9px;
        `}
    &:focus {
        outline: none;
        border: 2px solid #4a154b;
        padding: 9px 9px 9px 24px;
    }
`;

const Input = styled.input`
    padding: 10px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;

    ${({ error }) =>
        error &&
        css`
            border: 2px solid #d90000;
            padding: 9px;
        `}
    &:focus {
        outline: none;
        border: 2px solid #4a154b;
        padding: 9px;
    }
`;

const DualField = styled.div`
    display: flex;
    gap: 10px;

    & ${FieldContainer} {
        flex: 1;
    }

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

const PaymentSelect = styled.div`
    background: #f8f8f8;
    border-radius: 10px;
    border: 1px solid #f8f8f8;
    height: 32px;
    display: flex;
    align-items: center;
    padding: 20px;
    position: relative;

    & > :first-child {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: row;
        cursor: pointer !important;
        gap: 10px;
        font-weight: 600;
    }

    ${({ error }) =>
        error &&
        css`
            border: 1px solid #d90000;
        `}
`;

const PaymentOptions = styled.div`
    position: absolute;
    display: none;
    top: 100%;
    left: 0;
    right: 0;
    padding: 10px;
    flex-direction: column;
    background: #ffffff;
    box-shadow:
        0px 0px 8px rgba(0, 0, 0, 0.05),
        0px 16px 32px rgba(23, 37, 76, 0.12);
    border-radius: 12px;
    gap: 10px;
    z-index: 1;
    // If open
    ${({ isOpen }) =>
        isOpen === true &&
        css`
            display: flex;
        `}
`;

const PaymentOption = styled.div`
    padding: 10px;
    border-radius: 5px;
    font-style: normal;
    font-weight: 600;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    text-transform: capitalize;

    ${({ selected }) =>
        selected &&
        css`
            color: #7e57c2;
        `}
    &:hover {
        background: #f8f8f8;
    }
`;

const Prefix = styled.div`
    position: absolute;
    left: 10px;
    // center vertically
    display: flex;
    align-items: center;
    height: 100%;
    font-size: 20px;
    color: rgba(0, 0, 0, 0.54);
`;

const StyledTextField = styled(TextField)`
    & .MuiInputBase-root {
        height: 35px;
        padding: 12px;
        border-radius: 5px;
        border: none;
        font-family: 'Roboto';
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        box-sizing: border-box;
        background-color: white;

        &.Mui-focused > fieldset {
            border: 2px solid #4a154b;
        }

        &:focus {
            outline: none;
            border: 2px solid #4a154b;
            padding: 9px;
        }

        & fieldset {
            ${({ error }) =>
                error &&
                css`
                    border: 2px solid #d90000;
                    padding: 9px;
                `}
        }
    }
`;

export default Payment;
